import type { Feature, FeatureCollection } from 'geojson'
import type { Point, Polygon, MultiPolygon } from 'geojson'
import maplibregl from 'maplibre-gl'
import type { Map } from 'maplibre-gl'
import { MaplibreTerradrawControl, MaplibreMeasureControl } from '@watergis/maplibre-gl-terradraw'
import '@watergis/maplibre-gl-terradraw/dist/maplibre-gl-terradraw.css'
import {
  area,
  booleanPointInPolygon,
  point as turfPoint,
  lineString as turfLineString,
  pointToLineDistance,
} from '@turf/turf'
import {
  type TerraDraw,
  TerraDrawLineStringMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawCircleMode,
  TerraDrawFreehandMode,
  TerraDrawAngledRectangleMode,
  TerraDrawSectorMode,
} from 'terra-draw'
import { type Ref, computed, onBeforeUnmount, ref, shallowRef, toRaw, watch } from 'vue'
import { ItemType } from '@/models/enums/app/ItemType'

const SQUARE_METERS_TO_SQUARE_KM = 1e6
const AREA_DECIMAL_PLACES = 2

const POINT_MARKER_SOURCE_ID = 'terradraw-point-markers'
const POINT_MARKER_LAYER_ID = 'terradraw-point-markers-layer'
const POINT_MARKER_IMAGE_ID = 'terradraw-point-pin'

const HOVER_DELETE_LINE_METERS = 20
const HOVER_HIGHLIGHT_SOURCE_ID = 'terradraw-hover-highlight'
const HOVER_HIGHLIGHT_FILL_LAYER_ID = 'terradraw-hover-highlight-fill'
const HOVER_HIGHLIGHT_LINE_LAYER_ID = 'terradraw-hover-highlight-line'

const MEASURE_LINE_LAYER_ID = 'td-measure-linestring'
const MEASURE_POLYGON_OUTLINE_LAYER_ID = 'td-measure-polygon-outline'
const MEASURE_POLYGON_FILL_LAYER_ID = 'td-measure-polygon'
const MEASURE_DASH_PATTERN: [number, number] = [2, 2]
const MEASURE_FILL_OPACITY = 0.15

/** Layers used for project / actor / resource markers so TerraDraw hover logic does not force grab over them. */
function listPlatformMarkerInteractionLayerIds(rawMap: Map): string[] {
  return [
    `${ItemType.RESOURCE}-hit`,
    ItemType.RESOURCE,
    `${ItemType.PROJECT}-hit`,
    ItemType.PROJECT,
    `${ItemType.ACTOR}-hit`,
    ItemType.ACTOR,
  ].filter((layerId) => Boolean(rawMap.getLayer(layerId)))
}

function getLastPointLngLat(feature: Feature & { id?: string }): [number, number] | null {
  const geom = feature.geometry
  if (!geom) return null
  const toLngLat = (p: number[]): [number, number] => [Number(p[0]), Number(p[1])]
  if (geom.type === 'Point') return toLngLat(geom.coordinates)
  if (geom.type === 'LineString') {
    const c = geom.coordinates
    return c.length > 0 ? toLngLat(c[c.length - 1]) : null
  }
  if (geom.type === 'Polygon') {
    const ring = geom.coordinates[0]
    if (!ring || ring.length < 2) return null
    return toLngLat(ring[ring.length - 2])
  }
  if (geom.type === 'MultiPolygon') {
    const lastPoly = geom.coordinates[geom.coordinates.length - 1]
    const ring = lastPoly?.[0]
    if (!ring || ring.length < 2) return null
    return toLngLat(ring[ring.length - 2])
  }
  return null
}

function getFeatureIdAtLngLat(
  lngLat: [number, number],
  features: (Feature & { id?: string })[],
): string | null {
  const pt = turfPoint(lngLat)
  for (const f of features) {
    if ((f.properties as Record<string, unknown> | null)?.currentlyDrawing) continue
    const id = f.id != null ? String(f.id) : null
    if (!id) continue
    const geom = f.geometry
    if (!geom || geom.type === 'GeometryCollection') continue
    if (geom.type === 'Point') continue
    if (geom.type === 'LineString') {
      const line = turfLineString(geom.coordinates)
      const dist = pointToLineDistance(pt, line, { units: 'meters' })
      if (dist <= HOVER_DELETE_LINE_METERS) return id
    } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
      const polyFeature: Feature<Polygon | MultiPolygon> = {
        type: 'Feature',
        geometry: geom,
        properties: f.properties ?? {},
      }
      if (booleanPointInPolygon(pt, polyFeature)) return id
    }
  }
  return null
}

const DRAW_LINE_LAYER_ID = 'td-linestring'
const DRAW_POLYGON_OUTLINE_LAYER_ID = 'td-polygon-outline'

function applyMeasureDashStyle(rawMap: Map) {
  try {
    if (rawMap.getLayer(MEASURE_LINE_LAYER_ID)) {
      const current = rawMap.getPaintProperty(MEASURE_LINE_LAYER_ID, 'line-dasharray')
      if (!current)
        rawMap.setPaintProperty(MEASURE_LINE_LAYER_ID, 'line-dasharray', MEASURE_DASH_PATTERN)
    }
    if (rawMap.getLayer(MEASURE_POLYGON_OUTLINE_LAYER_ID)) {
      const current = rawMap.getPaintProperty(MEASURE_POLYGON_OUTLINE_LAYER_ID, 'line-dasharray')
      if (!current)
        rawMap.setPaintProperty(
          MEASURE_POLYGON_OUTLINE_LAYER_ID,
          'line-dasharray',
          MEASURE_DASH_PATTERN,
        )
    }
    if (rawMap.getLayer(MEASURE_POLYGON_FILL_LAYER_ID)) {
      const current = rawMap.getPaintProperty(MEASURE_POLYGON_FILL_LAYER_ID, 'fill-opacity')
      if (current !== MEASURE_FILL_OPACITY)
        rawMap.setPaintProperty(MEASURE_POLYGON_FILL_LAYER_ID, 'fill-opacity', MEASURE_FILL_OPACITY)
    }
  } catch {
    /* layers may not exist yet */
  }
}

function ensureDrawSolidStyle(rawMap: Map) {
  try {
    if (rawMap.getLayer(DRAW_LINE_LAYER_ID)) {
      const current = rawMap.getPaintProperty(DRAW_LINE_LAYER_ID, 'line-dasharray')
      if (current) rawMap.setPaintProperty(DRAW_LINE_LAYER_ID, 'line-dasharray', undefined)
    }
    if (rawMap.getLayer(DRAW_POLYGON_OUTLINE_LAYER_ID)) {
      const current = rawMap.getPaintProperty(DRAW_POLYGON_OUTLINE_LAYER_ID, 'line-dasharray')
      if (current)
        rawMap.setPaintProperty(DRAW_POLYGON_OUTLINE_LAYER_ID, 'line-dasharray', undefined)
    }
  } catch {
    /* ignore */
  }
}

const PIN_WIDTH = 50
const PIN_HEIGHT = 50

function createPinImageData(): ImageData {
  const canvas = document.createElement('canvas')
  canvas.width = PIN_WIDTH
  canvas.height = PIN_HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) return new ImageData(PIN_WIDTH, PIN_HEIGHT)

  const cx = PIN_WIDTH / 2
  const headRadius = 16
  const headCenterY = headRadius + 4
  const tipY = PIN_HEIGHT - 4

  // Drop shadow
  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.3)'
  ctx.shadowBlur = 5
  ctx.shadowOffsetY = 2

  // Teardrop shape
  ctx.beginPath()
  ctx.moveTo(cx, tipY)
  ctx.bezierCurveTo(
    cx - 5,
    headCenterY + headRadius * 1.5,
    cx - headRadius,
    headCenterY + headRadius * 0.5,
    cx - headRadius,
    headCenterY,
  )
  ctx.arc(cx, headCenterY, headRadius, Math.PI, 0, false)
  ctx.bezierCurveTo(
    cx + headRadius,
    headCenterY + headRadius * 0.5,
    cx + 5,
    headCenterY + headRadius * 1.5,
    cx,
    tipY,
  )
  ctx.closePath()

  // Solid orange fill matching IGN style
  ctx.fillStyle = '#f59e42'
  ctx.fill()
  ctx.strokeStyle = '#d4820e'
  ctx.lineWidth = 1
  ctx.stroke()
  ctx.restore()

  // White inner circle
  ctx.beginPath()
  ctx.arc(cx, headCenterY, headRadius * 0.42, 0, Math.PI * 2)
  ctx.fillStyle = '#ffffff'
  ctx.fill()

  return ctx.getImageData(0, 0, PIN_WIDTH, PIN_HEIGHT)
}

const TO_TERRA_MODE: Record<string, string> = {
  draw_point: 'point',
  draw_line_string: 'linestring',
  draw_polygon: 'polygon',
  simple_select: 'select',
  direct_select: 'select',
  rectangle: 'rectangle',
  circle: 'circle',
  freehand: 'freehand',
  'angled-rectangle': 'angled-rectangle',
  sensor: 'sensor',
  sector: 'sector',
  delete: 'delete',
  download: 'download',
}

const FROM_TERRA_MODE: Record<string, string> = {
  point: 'draw_point',
  linestring: 'draw_line_string',
  polygon: 'draw_polygon',
  select: 'simple_select',
  rectangle: 'rectangle',
  circle: 'circle',
  freehand: 'freehand',
  'angled-rectangle': 'angled-rectangle',
  sensor: 'sensor',
  sector: 'sector',
}

export type DrawMode =
  | 'draw_polygon'
  | 'draw_line_string'
  | 'draw_point'
  | 'simple_select'
  | 'direct_select'
  | 'rectangle'
  | 'circle'
  | 'freehand'
  | 'angled-rectangle'
  | 'sensor'
  | 'sector'
  | 'delete'
  | 'download'
  | 'measure_distance'
  | 'measure_area'

export interface PointAnnotation {
  title: string
  description: string
}

export interface UseMapDrawReturn {
  draw: Readonly<{ value: TerraDraw | null }>
  drawFeatures: Readonly<{ value: FeatureCollection }>
  selectedFeatureIds: Readonly<{ value: string[] }>
  polygonAreaKm2: Readonly<{ value: number | null }>
  polygonAreaM2: Readonly<{ value: number | null }>
  isDrawActive: Readonly<{ value: boolean }>
  currentDrawMode: Ref<string>
  isMeasuring: Ref<boolean>
  pointAnnotations: Readonly<{ value: Record<string, PointAnnotation> }>
  init: (mapInstance: Map) => void
  changeMode: (mode: DrawMode) => void
  trash: () => void
  getMode: () => string
  activate: () => void
  deactivate: () => void
  download: () => void
  setPointAnnotation: (featureId: string, annotation: PointAnnotation) => void
  getPointAnnotation: (featureId: string) => PointAnnotation | undefined
  startMeasureDistance: () => void
  startMeasureArea: () => void
  stopMeasure: () => void
  restoreFeatures: (features: Feature[], annotations: Record<string, PointAnnotation>) => void
  removeFeatureById: (featureId: string) => void
}

export function computeAreaKm2FromFeatures(features: Feature[]): number | null {
  const polygonFeatures = features.filter(
    (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon',
  )
  if (polygonFeatures.length === 0) return null
  let totalAreaM2 = 0
  for (const feature of polygonFeatures) {
    totalAreaM2 += area(feature)
  }
  const areaKm2 = totalAreaM2 / SQUARE_METERS_TO_SQUARE_KM
  return Math.round(areaKm2 * Math.pow(10, AREA_DECIMAL_PLACES)) / Math.pow(10, AREA_DECIMAL_PLACES)
}

export function computeAreaM2FromFeatures(features: Feature[]): number | null {
  const polygonFeatures = features.filter(
    (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon',
  )
  if (polygonFeatures.length === 0) return null
  let total = 0
  for (const feature of polygonFeatures) {
    total += area(feature)
  }
  return Math.round(total * 100) / 100
}

const STYLE_LOAD_REINIT_KEY = '__mapDrawStyleLoadReinit'
const TERRADRAW_HIDE_CSS_ID = 'terradraw-hide-controls-css'

function ensureTerraDrawHideCSS() {
  if (document.getElementById(TERRADRAW_HIDE_CSS_ID)) return
  const style = document.createElement('style')
  style.id = TERRADRAW_HIDE_CSS_ID
  style.textContent = `
    .maplibregl-terradraw-add-control,
    .maplibregl-terradraw-render-button,
    .maplibregl-terradraw-measure-add-control,
    .maplibregl-terradraw-measure-render-button { display: none !important; }
  `
  document.head.appendChild(style)
}

const POPUP_CSS = `
.MapDrawPointPopup { min-width: 260px; max-width: 320px; padding: 16px; font-family: inherit; }
.MapDrawPointPopup__label { display: block; margin: 0 0 4px 0; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #555; }
.MapDrawPointPopup__input, .MapDrawPointPopup__textarea { width: 100%; padding: 6px 8px; margin-bottom: 8px; font-size: 13px; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
.MapDrawPointPopup__textarea { min-height: 60px; resize: vertical; }
.MapDrawPointPopup__coords { margin: 0 0 8px 0; font-size: 11px; color: #666; }
.MapDrawPointPopup__btn { width: 100%; padding: 8px 12px; font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #fff; background: rgb(var(--v-theme-main-blue)); border: none; border-radius: 4px; cursor: pointer; }
.MapDrawPointPopup__btn:hover { filter: brightness(1.08); }
.MapDrawPointPopup__deleteWrap { margin: 8px 0 0 0; text-align: center; }
.MapDrawPointPopup__deleteLink { font-style: italic; color: rgb(var(--v-theme-main-blue)); text-decoration: none; }
.MapDrawPointPopup__deleteLink:hover { text-decoration: underline; }
`

function buildPointAnnotationPopupContent(
  lng: number,
  lat: number,
  initial: PointAnnotation | undefined,
  onSave: (title: string, description: string) => void,
  onDelete?: (featureId: string) => void,
  featureId?: string,
): HTMLDivElement {
  const wrap = document.createElement('div')
  wrap.className = 'MapDrawPointPopup'
  const style = document.createElement('style')
  style.textContent = POPUP_CSS
  wrap.appendChild(style)

  const titleLabel = document.createElement('label')
  titleLabel.className = 'MapDrawPointPopup__label'
  titleLabel.textContent = 'Nom du point'
  wrap.appendChild(titleLabel)
  const titleInput = document.createElement('input')
  titleInput.className = 'MapDrawPointPopup__input'
  titleInput.type = 'text'
  titleInput.placeholder = 'Saisir un titre...'
  titleInput.value = initial?.title ?? ''
  wrap.appendChild(titleInput)

  const descLabel = document.createElement('label')
  descLabel.className = 'MapDrawPointPopup__label'
  descLabel.textContent = 'Description'
  wrap.appendChild(descLabel)
  const descInput = document.createElement('textarea')
  descInput.className = 'MapDrawPointPopup__textarea'
  descInput.placeholder = 'Saisir une description...'
  descInput.rows = 3
  descInput.value = initial?.description ?? ''
  wrap.appendChild(descInput)

  const coords = document.createElement('p')
  coords.className = 'MapDrawPointPopup__coords'
  coords.textContent = `lon : ${lng.toFixed(4)}° / lat : ${lat.toFixed(4)}°`
  wrap.appendChild(coords)

  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = 'MapDrawPointPopup__btn'
  btn.textContent = 'ENREGISTRER'
  btn.addEventListener('click', () => {
    onSave(titleInput.value.trim(), descInput.value.trim())
  })
  wrap.appendChild(btn)

  if (onDelete && featureId) {
    const deleteWrap = document.createElement('p')
    deleteWrap.className = 'MapDrawPointPopup__deleteWrap'
    const deleteLink = document.createElement('a')
    deleteLink.href = '#'
    deleteLink.className = 'MapDrawPointPopup__deleteLink'
    deleteLink.textContent = 'Supprimer le marker'
    deleteLink.addEventListener('click', (ev) => {
      ev.preventDefault()
      onDelete(featureId)
    })
    deleteWrap.appendChild(deleteLink)
    wrap.appendChild(deleteWrap)
  }

  return wrap
}

function showPointAnnotationPopup(
  map: Map,
  lngLat: [number, number],
  featureId: string,
  initial: PointAnnotation | undefined,
  setPointAnnotation: (id: string, a: PointAnnotation) => void,
  onDelete?: (featureId: string, closePopup: () => void) => void,
): void {
  const popup = new maplibregl.Popup({ closeButton: true, closeOnClick: false, className: 'show' })
  const closePopup = () => popup.remove()
  const content = buildPointAnnotationPopupContent(
    lngLat[0],
    lngLat[1],
    initial,
    (title, description) => {
      setPointAnnotation(featureId, { title, description })
      closePopup()
    },
    onDelete ? (id: string) => onDelete(id, closePopup) : undefined,
    featureId,
  )
  popup.setDOMContent(content)
  popup.setLngLat(lngLat).addTo(map)
}

function getMapInstance(
  raw: Map | null | undefined | { value?: Map | null },
): Map | null | undefined {
  if (raw == null) return raw
  if (
    typeof raw === 'object' &&
    'value' in raw &&
    raw.value != null &&
    typeof (raw.value as Map).addControl === 'function'
  ) {
    return raw.value as Map
  }
  return raw as Map
}

export function useMapDraw(mapRef: { value: Map | null | undefined }): UseMapDrawReturn {
  const draw = shallowRef<TerraDraw | null>(null)
  const controlRef = shallowRef<InstanceType<typeof MaplibreTerradrawControl> | null>(null)
  const mapInstanceRef = shallowRef<Map | null>(null)
  const drawFeatures = shallowRef<FeatureCollection>({ type: 'FeatureCollection', features: [] })
  const selectedFeatureIds = shallowRef<string[]>([])
  const polygonAreaKm2 = shallowRef<number | null>(null)
  const isDrawActive = ref(false)
  const currentDrawMode = ref('')
  const isMeasuring = ref(false)
  const measureControlRef = shallowRef<InstanceType<typeof MaplibreMeasureControl> | null>(null)
  const pointAnnotations = shallowRef<Record<string, PointAnnotation>>({})
  let _measureDashHandler: (() => void) | null = null

  const polygonAreaM2 = computed(() => {
    const km2 = polygonAreaKm2.value
    if (km2 == null) return null
    return Math.round(km2 * SQUARE_METERS_TO_SQUARE_KM * 100) / 100
  })

  let _changeDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let _mapClickHandler: (() => void) | null = null
  let _hoverDeletePopup: maplibregl.Popup | null = null
  let _hoverDeleteMouseMoveHandler: ((e: maplibregl.MapMouseEvent) => void) | null = null
  let _hoveredFeatureId: string | null = null
  let _hoverHideTimer: ReturnType<typeof setTimeout> | null = null
  const HOVER_HIDE_DELAY_MS = 800

  function updateFromTerraDraw() {
    const td = draw.value
    const ctrl = controlRef.value
    if (!td || !ctrl) return
    const snapshot = td.getSnapshot()
    drawFeatures.value = { type: 'FeatureCollection', features: snapshot }
    const featuresResult = ctrl.getFeatures(true)
    const selected = featuresResult?.features ?? []
    selectedFeatureIds.value = selected.map((f: { id?: string | number }) => String(f.id))
    polygonAreaKm2.value = computeAreaKm2FromFeatures(selected as Feature[])
    const rawMap = mapInstanceRef.value ? toRaw(mapInstanceRef.value) : null
    if (rawMap) syncPointMarkerSource(rawMap)
  }

  function syncPointMarkerSourceImmediate() {
    const rawMap = mapInstanceRef.value ? toRaw(mapInstanceRef.value) : null
    if (rawMap) _syncPointMarkerSourceNow(rawMap)
  }

  function debouncedUpdateFromTerraDraw() {
    if (_changeDebounceTimer) clearTimeout(_changeDebounceTimer)
    _changeDebounceTimer = setTimeout(updateFromTerraDraw, 100)
  }

  let _syncRAF: number | null = null

  function syncPointMarkerSource(rawMap: Map) {
    if (_syncRAF) return
    _syncRAF = requestAnimationFrame(() => {
      _syncRAF = null
      _syncPointMarkerSourceNow(rawMap)
    })
  }

  function _syncPointMarkerSourceNow(rawMap: Map) {
    const td = draw.value
    if (!td) return
    const source = rawMap.getSource(POINT_MARKER_SOURCE_ID) as
      | { setData: (data: FeatureCollection) => void }
      | undefined
    if (!source || typeof source.setData !== 'function') return
    const snapshot = td.getSnapshot()
    const annotations = pointAnnotations.value
    const pointFeatures = snapshot
      .filter((f) => f.geometry?.type === 'Point' && f.properties?.mode === 'point')
      .map((f) => {
        const id = String((f as Feature & { id?: string }).id ?? '')
        const annotation = id ? annotations[id] : undefined
        return {
          ...f,
          properties: {
            ...(f.properties || {}),
            id,
            ...(annotation?.title ? { title: annotation.title } : {}),
          },
        }
      })
    source.setData({ type: 'FeatureCollection', features: pointFeatures })
    if (rawMap.getLayer(POINT_MARKER_LAYER_ID)) {
      rawMap.moveLayer(POINT_MARKER_LAYER_ID)
    }
  }

  const _hiddenTdCircleLayers = new Set<string>()

  function hideTerraDrawPointCircles(rawMap: Map) {
    const style = rawMap.getStyle()
    if (!style?.layers) return
    for (const layer of style.layers) {
      if (layer.type !== 'circle') continue
      if (_hiddenTdCircleLayers.has(layer.id)) continue
      const src = 'source' in layer ? String(layer.source ?? '') : ''
      if (!src.startsWith('td-') || src.startsWith('td-measure')) continue
      try {
        rawMap.setPaintProperty(layer.id, 'circle-opacity', 0)
        rawMap.setPaintProperty(layer.id, 'circle-stroke-opacity', 0)
        _hiddenTdCircleLayers.add(layer.id)
      } catch {
        /* ignore */
      }
    }
  }

  function _addControl(mapInstance: Map) {
    const rawMap = toRaw(mapInstance) as Map
    const IGN_POLYGON_STYLE = {
      fillColor: '#5f9ea0' as const,
      fillOpacity: 0.45,
      outlineColor: '#2c3e50' as const,
      outlineWidth: 3,
      closingPointColor: '#ffffff' as const,
      closingPointWidth: 3,
      closingPointOutlineColor: '#2c3e50' as const,
      closingPointOutlineWidth: 1,
    }

    const IGN_LINE_STYLE = {
      lineStringColor: '#1a1a1a' as const,
      lineStringWidth: 3,
      closingPointColor: '#ffffff' as const,
      closingPointWidth: 3,
      closingPointOutlineColor: '#1a1a1a' as const,
      closingPointOutlineWidth: 1,
    }

    const ctrl = new MaplibreTerradrawControl({
      open: false,
      modes: [
        'point',
        'linestring',
        'polygon',
        'rectangle',
        'circle',
        'freehand',
        'angled-rectangle',
        'sensor',
        'sector',
        'select',
        'delete-selection',
        'delete',
        'download',
      ],
      modeOptions: {
        linestring: new TerraDrawLineStringMode({ styles: IGN_LINE_STYLE }),
        polygon: new TerraDrawPolygonMode({ styles: IGN_POLYGON_STYLE }),
        rectangle: new TerraDrawRectangleMode({ styles: IGN_POLYGON_STYLE }),
        circle: new TerraDrawCircleMode({ styles: IGN_POLYGON_STYLE }),
        freehand: new TerraDrawFreehandMode({ styles: IGN_POLYGON_STYLE }),
        'angled-rectangle': new TerraDrawAngledRectangleMode({ styles: IGN_POLYGON_STYLE }),
        sector: new TerraDrawSectorMode({ styles: IGN_POLYGON_STYLE }),
      },
      showDeleteConfirmation: false,
    })
    rawMap.addControl(ctrl, 'top-left')
    ensureTerraDrawHideCSS()
    controlRef.value = ctrl

    const MEASURE_LINE_STYLE = {
      lineStringColor: '#e67e22' as const,
      lineStringWidth: 2,
      closingPointColor: '#ffffff' as const,
      closingPointWidth: 3,
      closingPointOutlineColor: '#e67e22' as const,
      closingPointOutlineWidth: 1,
    }
    const MEASURE_POLYGON_STYLE = {
      fillColor: '#e67e22' as const,
      fillOpacity: MEASURE_FILL_OPACITY,
      outlineColor: '#e67e22' as const,
      outlineWidth: 2,
      closingPointColor: '#ffffff' as const,
      closingPointWidth: 3,
      closingPointOutlineColor: '#e67e22' as const,
      closingPointOutlineWidth: 1,
    }

    const measureCtrl = new MaplibreMeasureControl({
      modes: ['linestring', 'polygon', 'delete'],
      open: false,
      modeOptions: {
        linestring: new TerraDrawLineStringMode({ styles: MEASURE_LINE_STYLE }),
        polygon: new TerraDrawPolygonMode({ styles: MEASURE_POLYGON_STYLE }),
      },
    })
    rawMap.addControl(measureCtrl, 'top-left')
    measureControlRef.value = measureCtrl

    const measureTd = measureCtrl.getTerraDrawInstance()
    if (measureTd) {
      measureTd.on('finish', () => {
        currentDrawMode.value = ''
        isMeasuring.value = false
        try {
          measureTd.setMode('static')
        } catch {
          /* ignore */
        }
      })
    }

    _measureDashHandler = () => {
      applyMeasureDashStyle(rawMap)
      ensureDrawSolidStyle(rawMap)
    }
    rawMap.on('idle', _measureDashHandler)

    mapInstanceRef.value = rawMap
    const td = ctrl.getTerraDrawInstance()
    draw.value = td ?? null
    if (!td) return
    updateFromTerraDraw()

    if (!rawMap.hasImage(POINT_MARKER_IMAGE_ID)) {
      const pinImage = createPinImageData()
      rawMap.addImage(POINT_MARKER_IMAGE_ID, pinImage, { pixelRatio: 1 })
    }
    if (!rawMap.getSource(POINT_MARKER_SOURCE_ID)) {
      rawMap.addSource(POINT_MARKER_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (!rawMap.getLayer(POINT_MARKER_LAYER_ID)) {
      rawMap.addLayer({
        id: POINT_MARKER_LAYER_ID,
        type: 'symbol',
        source: POINT_MARKER_SOURCE_ID,
        layout: {
          'icon-image': POINT_MARKER_IMAGE_ID,
          'icon-size': 1,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
          'text-field': ['get', 'title'],
          'text-anchor': 'bottom',
          'text-offset': [0, -3.5],
          'text-size': 14,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#333',
          'text-halo-color': '#fff',
          'text-halo-width': 1.5,
        },
      })
    }
    syncPointMarkerSource(rawMap)
    if (rawMap.getLayer(POINT_MARKER_LAYER_ID)) {
      rawMap.moveLayer(POINT_MARKER_LAYER_ID)
    }

    if (!rawMap.getSource(HOVER_HIGHLIGHT_SOURCE_ID)) {
      rawMap.addSource(HOVER_HIGHLIGHT_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (!rawMap.getLayer(HOVER_HIGHLIGHT_FILL_LAYER_ID)) {
      rawMap.addLayer({
        id: HOVER_HIGHLIGHT_FILL_LAYER_ID,
        type: 'fill',
        source: HOVER_HIGHLIGHT_SOURCE_ID,
        paint: {
          'fill-color': '#2c3e50',
          'fill-opacity': 0.35,
        },
      })
    }
    if (!rawMap.getLayer(HOVER_HIGHLIGHT_LINE_LAYER_ID)) {
      rawMap.addLayer({
        id: HOVER_HIGHLIGHT_LINE_LAYER_ID,
        type: 'line',
        source: HOVER_HIGHLIGHT_SOURCE_ID,
        paint: {
          'line-color': '#5f9ea0',
          'line-width': 3,
        },
      })
    }

    let knownPointIds = new Set(
      (td.getSnapshot() as (Feature & { id?: string })[])
        .filter((f) => f.geometry?.type === 'Point' && f.properties?.mode === 'point')
        .map((f) => String(f.id ?? '')),
    )

    const POINT_DETECT_DELAY_MS = 150

    const onMapClick = (e: maplibregl.MapMouseEvent) => {
      const mode = td.getMode()

      if (mode === 'point') {
        setTimeout(() => {
          const snapshot = td.getSnapshot() as (Feature<Point> & { id?: string })[]
          const pointFeatures = snapshot.filter(
            (f) => f.geometry?.type === 'Point' && f.properties?.mode === 'point',
          )
          const currentIds = new Set(pointFeatures.map((f) => String(f.id ?? '')))
          const newPoints = pointFeatures.filter((f) => !knownPointIds.has(String(f.id ?? '')))
          knownPointIds = currentIds
          if (newPoints.length === 0) return
          const newPoint = newPoints[newPoints.length - 1]
          const coords = newPoint.geometry.coordinates
          const featureId = String(newPoint.id ?? '')
          if (!featureId) return
          if (_changeDebounceTimer) clearTimeout(_changeDebounceTimer)
          try {
            td.setMode('select')
          } catch {
            /* ignore */
          }
          currentDrawMode.value = ''
          rawMap.getCanvas().style.cursor = 'grab'
          debouncedUpdateFromTerraDraw()
          showPointAnnotationPopup(
            rawMap,
            [coords[0], coords[1]],
            featureId,
            getPointAnnotation(featureId),
            setPointAnnotation,
            (id, closePopup) => {
              const next = { ...pointAnnotations.value }
              delete next[id]
              pointAnnotations.value = next
              td.removeFeatures([id])
              updateFromTerraDraw()
              closePopup()
            },
          )
        }, POINT_DETECT_DELAY_MS)
        return
      }

      if (!rawMap.getLayer(POINT_MARKER_LAYER_ID)) return
      const markerFeatures = rawMap.queryRenderedFeatures(e.point, {
        layers: [POINT_MARKER_LAYER_ID],
      })
      if (markerFeatures.length === 0) return
      const hit = markerFeatures[0]
      const featureId = String(hit.id ?? (hit.properties as Record<string, unknown>)?.id ?? '')
      if (!featureId) return
      const geom = hit.geometry as Point
      const coords = geom.coordinates
      showPointAnnotationPopup(
        rawMap,
        [coords[0], coords[1]],
        featureId,
        getPointAnnotation(featureId),
        setPointAnnotation,
        (id, closePopup) => {
          const next = { ...pointAnnotations.value }
          delete next[id]
          pointAnnotations.value = next
          td.removeFeatures([id])
          updateFromTerraDraw()
          closePopup()
        },
      )
    }

    rawMap.on('click', onMapClick)
    _mapClickHandler = onMapClick as () => void

    rawMap.on('mouseenter', POINT_MARKER_LAYER_ID, () => {
      rawMap.getCanvas().style.cursor = 'grab'
    })
    rawMap.on('mouseleave', POINT_MARKER_LAYER_ID, () => {
      rawMap.getCanvas().style.cursor = ''
    })

    td.on('change', () => {
      hideTerraDrawPointCircles(rawMap)
      debouncedUpdateFromTerraDraw()
    })

    td.on('finish', () => {
      const finishedMode = td.getMode()
      if (_changeDebounceTimer) clearTimeout(_changeDebounceTimer)
      const selected = ctrl.getFeatures(true)?.features ?? []
      for (const feat of selected) {
        const id = feat.id != null ? String(feat.id) : null
        if (id) td.deselectFeature(id)
      }
      if (finishedMode !== 'point') {
        try {
          td.setMode('select')
        } catch {
          /* ignore */
        }
        if (!isMeasuring.value) {
          currentDrawMode.value = ''
        }
        rawMap.getCanvas().style.cursor = 'grab'
      }
      updateFromTerraDraw()
      hideTerraDrawPointCircles(rawMap)
    })

    _hoverDeletePopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'show MapToolsPanel__hoverDeletePopup',
    })

    const DRAW_MODES = new Set([
      'point',
      'linestring',
      'polygon',
      'rectangle',
      'circle',
      'freehand',
      'angled-rectangle',
      'sensor',
      'sector',
    ])
    const hoverSource = rawMap.getSource(HOVER_HIGHLIGHT_SOURCE_ID) as
      | { setData: (data: FeatureCollection) => void }
      | undefined

    function clearHoverState() {
      _hoveredFeatureId = null
      _hoverDeletePopup?.remove()
      if (hoverSource?.setData) hoverSource.setData({ type: 'FeatureCollection', features: [] })
    }

    function scheduleHoverHide() {
      if (_hoverHideTimer) clearTimeout(_hoverHideTimer)
      _hoverHideTimer = setTimeout(() => {
        clearHoverState()
        _hoverHideTimer = null
      }, HOVER_HIDE_DELAY_MS)
    }

    const onHoverDeleteMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (isMeasuring.value) {
        if (_hoveredFeatureId) clearHoverState()
        return
      }
      const mode = td.getMode()
      if (DRAW_MODES.has(mode)) {
        if (_hoveredFeatureId) clearHoverState()
        return
      }
      const platformInteractionLayers = listPlatformMarkerInteractionLayerIds(rawMap)
      if (platformInteractionLayers.length > 0) {
        const platformHits = rawMap.queryRenderedFeatures(e.point, {
          layers: platformInteractionLayers,
        })
        if (platformHits.length > 0) {
          rawMap.getCanvas().style.cursor = 'pointer'
          if (_hoveredFeatureId) clearHoverState()
          return
        }
      }
      rawMap.getCanvas().style.cursor = 'grab'
      const snapshot = td.getSnapshot() as (Feature & { id?: string })[]
      const lngLat = [e.lngLat.lng, e.lngLat.lat] as [number, number]
      const featureId = getFeatureIdAtLngLat(lngLat, snapshot)
      if (featureId === _hoveredFeatureId) {
        if (featureId && _hoverHideTimer) {
          clearTimeout(_hoverHideTimer)
          _hoverHideTimer = null
        }
        return
      }
      if (featureId) {
        if (_hoverHideTimer) {
          clearTimeout(_hoverHideTimer)
          _hoverHideTimer = null
        }
      }
      _hoveredFeatureId = featureId
      if (hoverSource?.setData) {
        hoverSource.setData({
          type: 'FeatureCollection',
          features: featureId
            ? (snapshot.filter((f) => String(f.id) === featureId) as Feature[])
            : [],
        })
      }
      if (featureId && rawMap.getLayer(HOVER_HIGHLIGHT_FILL_LAYER_ID))
        rawMap.moveLayer(HOVER_HIGHLIGHT_FILL_LAYER_ID)
      if (featureId && rawMap.getLayer(HOVER_HIGHLIGHT_LINE_LAYER_ID))
        rawMap.moveLayer(HOVER_HIGHLIGHT_LINE_LAYER_ID)
      if (!_hoverDeletePopup) return
      if (!featureId) {
        scheduleHoverHide()
        return
      }
      const feature = snapshot.find((f) => String(f.id) === featureId)
      const anchorLngLat = feature ? getLastPointLngLat(feature) : null
      const popupLngLat = anchorLngLat ? { lng: anchorLngLat[0], lat: anchorLngLat[1] } : e.lngLat
      const div = document.createElement('div')
      div.className = 'MapToolsPanel__hoverDeleteWrap'
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'MapToolsPanel__hoverDeleteBtn'
      btn.title = 'Supprimer'
      btn.setAttribute('aria-label', 'Supprimer')
      btn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>'
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation()
        ev.preventDefault()
        const next = { ...pointAnnotations.value }
        delete next[featureId]
        pointAnnotations.value = next
        td.removeFeatures([featureId])
        updateFromTerraDraw()
        _hoverDeletePopup?.remove()
        _hoveredFeatureId = null
        if (hoverSource?.setData) hoverSource.setData({ type: 'FeatureCollection', features: [] })
      })
      div.appendChild(btn)
      _hoverDeletePopup.setDOMContent(div).setLngLat(popupLngLat).addTo(rawMap)
    }

    _hoverDeleteMouseMoveHandler = onHoverDeleteMouseMove
    rawMap.on('mousemove', onHoverDeleteMouseMove)
  }

  function setupDraw(mapInstance: Map) {
    if (controlRef.value) return
    const rawMap = toRaw(mapInstance) as Map
    try {
      _addControl(rawMap)
    } catch (err) {
      console.error('[useMapDraw] setupDraw failed', err)
    }
  }

  function init(mapInstance: Map) {
    if (!mapInstance) return
    if (controlRef.value) return
    const rawMap = toRaw(mapInstance) as Map
    try {
      _addControl(rawMap)
    } catch (err) {
      console.error('[useMapDraw] init failed', err)
    }
  }

  function removeDraw(mapInstance: Map) {
    const ctrl = controlRef.value
    if (!ctrl) return
    const rawMap = toRaw(mapInstance) as Map
    try {
      if (_hoverDeleteMouseMoveHandler) {
        rawMap.off('mousemove', _hoverDeleteMouseMoveHandler)
        _hoverDeleteMouseMoveHandler = null
      }
      _hoverDeletePopup?.remove()
      _hoverDeletePopup = null
      _hoveredFeatureId = null
      if (_hoverHideTimer) {
        clearTimeout(_hoverHideTimer)
        _hoverHideTimer = null
      }
      if (_mapClickHandler) {
        rawMap.off('click', _mapClickHandler)
        _mapClickHandler = null
      }
      if (_measureDashHandler) {
        rawMap.off('idle', _measureDashHandler)
        _measureDashHandler = null
      }
      if (rawMap.getLayer(HOVER_HIGHLIGHT_LINE_LAYER_ID))
        rawMap.removeLayer(HOVER_HIGHLIGHT_LINE_LAYER_ID)
      if (rawMap.getLayer(HOVER_HIGHLIGHT_FILL_LAYER_ID))
        rawMap.removeLayer(HOVER_HIGHLIGHT_FILL_LAYER_ID)
      if (rawMap.getSource(HOVER_HIGHLIGHT_SOURCE_ID))
        rawMap.removeSource(HOVER_HIGHLIGHT_SOURCE_ID)
      if (rawMap.getLayer(POINT_MARKER_LAYER_ID)) rawMap.removeLayer(POINT_MARKER_LAYER_ID)
      if (rawMap.getSource(POINT_MARKER_SOURCE_ID)) rawMap.removeSource(POINT_MARKER_SOURCE_ID)
      if (rawMap.hasImage(POINT_MARKER_IMAGE_ID)) rawMap.removeImage(POINT_MARKER_IMAGE_ID)
      rawMap.removeControl(ctrl)
      const mCtrl = measureControlRef.value
      if (mCtrl) {
        try {
          rawMap.removeControl(mCtrl)
        } catch {
          /* ignore */
        }
        measureControlRef.value = null
      }
    } catch {
      // Control may already be removed (e.g. after setStyle)
    }
    controlRef.value = null
    isMeasuring.value = false
    draw.value = null
    mapInstanceRef.value = null
    drawFeatures.value = { type: 'FeatureCollection', features: [] }
    selectedFeatureIds.value = []
    polygonAreaKm2.value = null
    isDrawActive.value = false
  }

  watch(
    () => getMapInstance(mapRef.value),
    (mapInstance) => {
      if (mapInstance) {
        const rawMap = toRaw(mapInstance) as Map
        setupDraw(rawMap)
        rawMap.on('style.load', () => {
          const reinit = (rawMap as Map & { [STYLE_LOAD_REINIT_KEY]?: boolean })[
            STYLE_LOAD_REINIT_KEY
          ]
          if (reinit && controlRef.value) {
            try {
              rawMap.removeControl(controlRef.value)
            } catch {
              // already removed by setStyle
            }
            controlRef.value = null
            draw.value = null
            _addControl(rawMap)
          }
          ;(rawMap as Map & { [STYLE_LOAD_REINIT_KEY]?: boolean })[STYLE_LOAD_REINIT_KEY] = true
        })
      } else if (controlRef.value) {
        draw.value = null
        controlRef.value = null
        mapInstanceRef.value = null
        drawFeatures.value = { type: 'FeatureCollection', features: [] }
        selectedFeatureIds.value = []
        polygonAreaKm2.value = null
        isDrawActive.value = false
      }
    },
    { immediate: true },
  )

  onBeforeUnmount(() => {
    const mapInstance = getMapInstance(mapRef.value)
    if (mapInstance && controlRef.value) {
      removeDraw(toRaw(mapInstance) as Map)
    }
  })

  function changeMode(mode: DrawMode) {
    stopMeasure()
    const td = draw.value
    const ctrl = controlRef.value
    if (!td || !ctrl) return
    const rawMap = toRaw(mapInstanceRef.value) as Map | null
    const terraMode = TO_TERRA_MODE[mode] ?? mode
    isDrawActive.value = mode.startsWith('draw_')
    if (!td.enabled) {
      ctrl.activate()
      td.start()
    }
    td.setMode(terraMode)
    if (rawMap) ensureDrawSolidStyle(rawMap)
    currentDrawMode.value = mode
    updateFromTerraDraw()
  }

  function trash() {
    const td = draw.value
    if (!td) return
    const ids = selectedFeatureIds.value
    if (ids.length > 0) {
      const next = { ...pointAnnotations.value }
      for (const id of ids) delete next[id]
      pointAnnotations.value = next
      td.removeFeatures(ids)
      updateFromTerraDraw()
    }
  }

  function removeFeatureById(featureId: string): void {
    const td = draw.value
    if (!td) return
    const next = { ...pointAnnotations.value }
    delete next[featureId]
    pointAnnotations.value = next
    td.removeFeatures([featureId])
    updateFromTerraDraw()
  }

  function getMode(): string {
    const td = draw.value
    if (!td) return ''
    const terraMode = td.getMode()
    return FROM_TERRA_MODE[terraMode] ?? terraMode
  }

  function activate() {
    controlRef.value?.activate()
    const td = draw.value
    if (td && !td.enabled) {
      td.start()
    }
  }

  function deactivate() {
    stopMeasure()
    pauseDrawControl()
    isDrawActive.value = false
    currentDrawMode.value = ''
  }

  function pauseDrawControl() {
    const td = draw.value
    if (td && td.enabled) {
      try {
        td.setMode('static')
      } catch {
        /* ignore */
      }
    }
  }

  function startMeasureDistance() {
    const mCtrl = measureControlRef.value
    if (!mCtrl) return
    const rawMap = toRaw(mapInstanceRef.value) as Map | null
    pauseDrawControl()
    mCtrl.activate()
    const mTd = mCtrl.getTerraDrawInstance()
    if (mTd) {
      if (!mTd.enabled) mTd.start()
      mTd.setMode('linestring')
    }
    isMeasuring.value = true
    currentDrawMode.value = 'measure_distance'
    if (rawMap) requestAnimationFrame(() => applyMeasureDashStyle(rawMap))
  }

  function startMeasureArea() {
    const mCtrl = measureControlRef.value
    if (!mCtrl) return
    const rawMap = toRaw(mapInstanceRef.value) as Map | null
    pauseDrawControl()
    mCtrl.activate()
    const mTd = mCtrl.getTerraDrawInstance()
    if (mTd) {
      if (!mTd.enabled) mTd.start()
      mTd.setMode('polygon')
    }
    isMeasuring.value = true
    currentDrawMode.value = 'measure_area'
    if (rawMap) requestAnimationFrame(() => applyMeasureDashStyle(rawMap))
  }

  function stopMeasure() {
    const mCtrl = measureControlRef.value
    if (!mCtrl) return
    const mTd = mCtrl.getTerraDrawInstance()
    if (mTd && mTd.enabled) {
      try {
        mTd.setMode('static')
      } catch {
        /* ignore */
      }
    }
    isMeasuring.value = false
    if (currentDrawMode.value.startsWith('measure_')) {
      currentDrawMode.value = ''
    }
  }

  function setPointAnnotation(featureId: string, annotation: PointAnnotation) {
    pointAnnotations.value = {
      ...pointAnnotations.value,
      [featureId]: { title: annotation.title, description: annotation.description },
    }
    syncPointMarkerSourceImmediate()
  }

  function getPointAnnotation(featureId: string): PointAnnotation | undefined {
    return pointAnnotations.value[featureId]
  }

  function restoreFeatures(
    features: Feature[],
    annotations: Record<string, PointAnnotation>,
  ): void {
    const td = draw.value
    if (!td || features.length === 0) return
    try {
      if (!td.enabled) {
        controlRef.value?.activate()
        td.start()
      }
      // TerraDraw's GeoJSONStoreFeatures is a strict subset of GeoJSON Feature — cast is safe here
      // because only features serialized from TerraDraw snapshots are passed.
      td.addFeatures(features as Parameters<typeof td.addFeatures>[0])
      if (Object.keys(annotations).length > 0) {
        pointAnnotations.value = { ...annotations }
      }
      updateFromTerraDraw()
    } catch (err) {
      console.error('[useMapDraw] restoreFeatures failed', err)
    }
  }

  function download() {
    const features = drawFeatures.value
    if (features.features.length === 0) return
    const annotations = pointAnnotations.value
    const featuresWithAnnotations = features.features.map((f) => {
      const id =
        (f as Feature & { id?: string }).id != null
          ? String((f as Feature & { id?: string }).id)
          : null
      const annotation = id ? annotations[id] : undefined
      if (!annotation) return f
      return {
        ...f,
        properties: {
          ...(f.properties || {}),
          title: annotation.title,
          description: annotation.description,
        },
      }
    })
    const fc: FeatureCollection = { type: 'FeatureCollection', features: featuresWithAnnotations }
    const json = JSON.stringify(fc, null, 2)
    const blob = new Blob([json], { type: 'application/geo+json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `annotations-${Date.now()}.geojson`
    a.click()
    URL.revokeObjectURL(url)
  }

  return {
    draw: draw as Readonly<{ value: TerraDraw | null }>,
    drawFeatures: drawFeatures as Readonly<{ value: FeatureCollection }>,
    selectedFeatureIds: selectedFeatureIds as Readonly<{ value: string[] }>,
    polygonAreaKm2: polygonAreaKm2 as Readonly<{ value: number | null }>,
    polygonAreaM2: polygonAreaM2 as Readonly<{ value: number | null }>,
    isDrawActive: isDrawActive as Readonly<{ value: boolean }>,
    currentDrawMode,
    isMeasuring,
    pointAnnotations: pointAnnotations as Readonly<{ value: Record<string, PointAnnotation> }>,
    init,
    changeMode,
    trash,
    getMode,
    activate,
    deactivate,
    download,
    setPointAnnotation,
    getPointAnnotation,
    startMeasureDistance,
    startMeasureArea,
    stopMeasure,
    restoreFeatures,
    removeFeatureById,
  }
}
